import { useEffect, useRef } from 'react';
import { trackFeatureArea, trackToolOpen } from '../utils/telemetry';

/**
 * useFeatureArea — Track entry/exit of a feature area.
 *
 * Emits feature_area_entered on mount, feature_area_exited on unmount.
 * Designed for major workspace sections (lesson_planner, admin_analytics, etc).
 *
 * @param {string} area – Feature area name (stable string, not dynamic)
 */
export function useFeatureArea(area) {
    useEffect(() => {
        if (!area) return;
        const cleanup = trackFeatureArea(area);
        return cleanup;
    }, [area]);
}

/**
 * useToolTracking — Track open/close of a tool or modal.
 *
 * Emits tool_opened when `isOpen` becomes true, tool_closed when it becomes false
 * or on unmount.
 *
 * @param {string} toolName – Tool identifier
 * @param {boolean} isOpen – Whether the tool is currently open
 */
export function useToolTracking(toolName, isOpen) {
    const cleanupRef = useRef(null);

    useEffect(() => {
        if (isOpen && !cleanupRef.current) {
            cleanupRef.current = trackToolOpen(toolName);
        } else if (!isOpen && cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }

        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, [isOpen, toolName]);
}
